import { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { 
  CreditCard, Home, History, User, Settings, Bell,
  CheckCircle, Filter, Download, Search, Globe, Wallet,
  ArrowUpCircle, ArrowDownCircle, Calendar, ChevronLeft, ChevronRight
} from "lucide-react";

export default function PlatiPomiruHistory() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const ITEMS_PER_PAGE = 10;
  
  // Get page from URL directly (not from state) to ensure it updates on navigation
  const pageParam = searchParams.get('page');
  const currentPage = pageParam ? Math.max(1, parseInt(pageParam) || 1) : 1;
  
  // Function to change page via URL
  const setCurrentPage = (page) => {
    navigate(`?page=${page}`, { replace: true });
  };

  useEffect(() => {
    const dataParam = searchParams.get('data');
    let parsed = null;
    
    if (dataParam) {
      try {
        parsed = JSON.parse(decodeURIComponent(dataParam));
      } catch (e) {}
    }
    
    if (!parsed) {
      try {
        const stored = localStorage.getItem('platipomiru_preview_data');
        if (stored) parsed = JSON.parse(stored);
      } catch (e) {}
    }
    
    if (parsed) {
      setData(parsed);
      
      // Calculate date range from transactions
      const transactions = parsed.transactions || [];
      if (transactions.length > 0) {
        const dates = transactions
          .map(tx => {
            if (!tx.date) return null;
            const match = tx.date.match(/(\d{2})\.(\d{2})\.(\d{4})/);
            if (!match) return null;
            const [, day, month, year] = match;
            return { 
              original: `${day}.${month}.${year}`, 
              sortValue: parseInt(year) * 10000 + parseInt(month) * 100 + parseInt(day) 
            };
          })
          .filter(d => d !== null);
        
        if (dates.length > 0) {
          dates.sort((a, b) => a.sortValue - b.sortValue);
          setDateFrom(dates[0].original);
          setDateTo(dates[dates.length - 1].original);
        }
      }
      
      // Use dateFrom/dateTo from data if provided
      if (parsed.dateFrom) setDateFrom(parsed.dateFrom);
      if (parsed.dateTo) setDateTo(parsed.dateTo);
    }
  }, [searchParams]);

  // Парсинг даты из строки "DD.MM.YYYY, HH:MM"
  const parseDate = (dateStr) => {
    if (!dateStr) return new Date(0);
    const match = dateStr.match(/(\d{2})\.(\d{2})\.(\d{4}),?\s*(\d{2}):(\d{2})/);
    if (match) {
      const [, day, month, year, hour, min] = match;
      return new Date(year, month - 1, day, hour, min);
    }
    return new Date(0);
  };

  // Filter by date range and type
  const filteredTransactions = useMemo(() => {
    if (!data) return [];
    return (data.transactions || []).filter(tx => {
      // Filter by type
      if (filter === 'topup' && tx.type !== 'topup') return false;
      if (filter === 'payment' && tx.type !== 'payment') return false;
      if (filter === 'withdrawal' && tx.type !== 'withdrawal') return false;
      
      // Filter by date range
      if (dateFrom && dateTo && tx.date) {
        const match = tx.date.match(/(\d{2})\.(\d{2})\.(\d{4})/);
        if (match) {
          const [, day, month, year] = match;
          const txSortValue = parseInt(year) * 10000 + parseInt(month) * 100 + parseInt(day);
          
          const fromParts = dateFrom.split('.');
          const fromSortValue = parseInt(fromParts[2]) * 10000 + parseInt(fromParts[1]) * 100 + parseInt(fromParts[0]);
          
          const toParts = dateTo.split('.');
          const toSortValue = parseInt(toParts[2]) * 10000 + parseInt(toParts[1]) * 100 + parseInt(toParts[0]);
          
          if (txSortValue < fromSortValue || txSortValue > toSortValue) return false;
        }
      }
      
      return true;
    }).sort((a, b) => parseDate(b.date) - parseDate(a.date)); // Сортировка: новые сверху
  }, [data, filter, dateFrom, dateTo]);

  // Pagination
  const totalPages = Math.ceil(filteredTransactions.length / ITEMS_PER_PAGE);
  const paginatedTransactions = filteredTransactions.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  if (!data) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-slate-500 text-lg">Загрузка...</div>
      </div>
    );
  }

  const menuItems = [
    { icon: Home, label: 'Главная' },
    { icon: CreditCard, label: 'Моя карта' },
    { icon: History, label: 'История', active: true },
    { icon: Wallet, label: 'Пополнить' },
    { icon: User, label: 'Профиль' },
    { icon: Settings, label: 'Настройки' },
  ];

  const totalTopups = (data.transactions || [])
    .filter(t => t.type === 'topup')
    .reduce((sum, t) => sum + (t.amountRub || 0), 0);

  const totalWithdrawals = (data.transactions || [])
    .filter(t => t.type === 'withdrawal')
    .reduce((sum, t) => sum + (t.amountRub || 0), 0);

  const totalPayments = (data.transactions || [])
    .filter(t => t.type === 'payment')
    .reduce((sum, t) => sum + (t.amount || 0), 0);

  return (
    <div className="min-h-screen bg-[#f8f9fb] template-preview-scale" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-violet-600 rounded-xl flex items-center justify-center">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="font-bold text-slate-800 text-lg">Плати по миру</span>
                <p className="text-xs text-slate-500">Virtual Cards</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <button className="relative p-2 text-slate-400 hover:text-slate-600">
                <Bell className="w-5 h-5" />
              </button>
              
              <div className="flex items-center gap-3 pl-4 border-l border-slate-200">
                <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-violet-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-bold">
                    {data.firstName?.[0]}{data.lastName?.[0]}
                  </span>
                </div>
                <div className="hidden sm:block">
                  <p className="text-sm font-medium text-slate-800">{data.fullName}</p>
                  <p className="text-xs text-slate-500">ID: {data.userId}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
              <nav className="space-y-1">
                {menuItems.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={index}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all ${
                        item.active 
                          ? 'bg-gradient-to-r from-blue-500 to-violet-600 text-white shadow-md' 
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-3 space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                    <ArrowUpCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-slate-500 text-sm">Пополнения</span>
                </div>
                <p className="text-2xl font-bold text-slate-800">+{totalTopups.toLocaleString()} ₽</p>
              </div>

              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                    <ArrowDownCircle className="w-5 h-5 text-orange-600" />
                  </div>
                  <span className="text-slate-500 text-sm">Выводы</span>
                </div>
                <p className="text-2xl font-bold text-slate-800">-{totalWithdrawals.toLocaleString()} ₽</p>
              </div>

              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="text-slate-500 text-sm">Оплаты</span>
                </div>
                <p className="text-2xl font-bold text-slate-800">-{totalPayments.toFixed(2)} $</p>
              </div>

              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
                    <History className="w-5 h-5 text-violet-600" />
                  </div>
                  <span className="text-slate-500 text-sm">Операций</span>
                </div>
                <p className="text-2xl font-bold text-slate-800">{data.transactions?.length || 0}</p>
              </div>
            </div>

            {/* Transactions List */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              {/* Filters */}
              <div className="p-4 border-b border-slate-100">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <h2 className="text-xl font-bold text-slate-800">История операций</h2>
                  
                  <div className="flex items-center gap-3">
                    {/* Date Filter */}
                    {dateFrom && dateTo && (
                      <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-blue-50 to-violet-50 border border-blue-200 rounded-lg">
                        <Calendar className="w-4 h-4 text-blue-600" />
                        <span className="text-sm text-blue-700 font-medium">
                          {dateTo} — {dateFrom}
                        </span>
                      </div>
                    )}

                    {/* Filter Pills */}
                    <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg">
                      {[
                        { id: 'all', label: 'Все' },
                        { id: 'topup', label: 'Пополнения' },
                        { id: 'withdrawal', label: 'Выводы' },
                        { id: 'payment', label: 'Оплаты' },
                      ].map(f => (
                        <button
                          key={f.id}
                          onClick={() => setFilter(f.id)}
                          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            filter === f.id 
                              ? 'bg-white text-slate-800 shadow-sm' 
                              : 'text-slate-500 hover:text-slate-700'
                          }`}
                        >
                          {f.label}
                        </button>
                      ))}
                    </div>

                    <button className="p-2 text-slate-400 hover:text-slate-600 border border-slate-200 rounded-lg">
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Transactions Table */}
              <div className="overflow-x-auto">
                <table className="w-full table-fixed">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="w-[35%] px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Операция</th>
                      <th className="w-[12%] px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Тип</th>
                      <th className="w-[15%] px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Сумма</th>
                      <th className="w-[15%] px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Статус</th>
                      <th className="w-[18%] px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Дата</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {paginatedTransactions.map((tx, index) => (
                      <tr key={index} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center text-lg ${
                              tx.type === 'topup' ? 'bg-green-100' : tx.type === 'withdrawal' ? 'bg-orange-100' : 'bg-slate-100'
                            }`}>
                              {tx.type === 'topup' ? '💳' : tx.type === 'withdrawal' ? '💸' : tx.icon}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-slate-800 truncate">{tx.description}</p>
                              {data.showSenders !== false && (
                                <p className="text-xs text-slate-500 truncate">
                                  {tx.type === 'topup' ? `Отправитель: ${tx.sender || 'СБП'}` : tx.type === 'withdrawal' ? `Отправитель: ${tx.recipient || 'Карта'}` : 'Онлайн сервис'}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                            tx.type === 'topup' 
                              ? 'bg-green-100 text-green-700' 
                              : tx.type === 'withdrawal'
                              ? 'bg-orange-100 text-orange-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {tx.type === 'topup' ? 'Пополнение' : tx.type === 'withdrawal' ? 'Вывод' : 'Оплата'}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-right">
                          <span className={`font-semibold whitespace-nowrap ${tx.type === 'topup' ? 'text-green-600' : tx.type === 'withdrawal' ? 'text-orange-600' : 'text-slate-700'}`}>
                            {tx.type === 'topup' ? `+${tx.amountRub?.toLocaleString()} ₽` : tx.type === 'withdrawal' ? `-${tx.amountRub?.toLocaleString()} ₽` : `-${tx.amount} ${tx.currency}`}
                          </span>
                          {(tx.type === 'topup' || tx.type === 'withdrawal') && (
                            <p className="text-xs text-slate-400">≈ {tx.amountUsd} $</p>
                          )}
                        </td>
                        <td className="px-4 py-4 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            <span className="text-sm text-green-600">Выполнено</span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-right">
                          <div className="flex items-center justify-end gap-2 text-slate-500 text-sm whitespace-nowrap">
                            <Calendar className="w-4 h-4 flex-shrink-0" />
                            {tx.date}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredTransactions.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-slate-400">Операции не найдены</p>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100">
                  <div className="text-sm text-slate-500">
                    Показано {(currentPage - 1) * ITEMS_PER_PAGE + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, filteredTransactions.length)} из {filteredTransactions.length}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === 1
                          ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                          : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Назад
                    </button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                            currentPage === page
                              ? 'bg-gradient-to-r from-blue-500 to-violet-600 text-white shadow-md'
                              : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                    </div>
                    
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === totalPages
                          ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                          : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      Вперёд
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
